import { useState } from "react";
import { bowlerBalls, bowlerOvers, oversText, personName, runRate, strikeRate } from "./derive";
import type { Inn } from "./derive";
import { Empty, NameLink, Seg } from "./shared";
import { innOpts } from "./innOpts";
import type { Ctx } from "./shared";

const ord = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const roleTag = (role?: string | null) => {
  const r = (role ?? "").toUpperCase();
  if (r === "CAPTAIN" || r === "TEAM_CAPTAIN") return "C";
  if (r.includes("VICE")) return "VC";
  if (r.includes("WICKET")) return "WK";
  return "";
};

const ScorecardPane = ({ ctx }: { ctx: Ctx }) => {
  const { inns, comms, squads, teams } = ctx;
  const [cur, setCur] = useState("0");
  const idx = Math.min(Number(cur), Math.max(inns.length - 1, 0));
  const inn: Inn | undefined = inns[idx];
  if (!inn) {
    return (
      <section className="pane on" id="pane-scorecard" role="tabpanel" aria-labelledby="tab-scorecard">
        <div className="box"><Empty>The scorecard is not available for this match yet.</Empty></div>
      </section>
    );
  }
  const comm = comms[idx];
  const squad = squads[teams[0] === inn.team ? 0 : 1]?.playingSquad ?? [];
  const roleOf = (id: string) => squad.find((p) => p.playerId === id)?.playerRole;
  const batted = new Set(inn.batting.map((b) => b.playerId));
  const dnb = inn.wkts >= 10 ? [] : squad.filter((p) => p.playerId && !batted.has(p.playerId));
  const maxPartner = comm ? Math.max(...comm.partnerships.map((p) => p.runs), 1) : 1;
  const fallRaw = (inn.sc.fallOfWickets as unknown as (string | number)[] | undefined) ?? [];
  const isChase = idx === 1 && inns.length === 2 && !ctx.multiDay;
  const note = ctx.multiDay ? "" : idx === 0 && inns.length > 1 ? `Target set: ${inn.runs + 1}` : isChase ? `Target was ${inns[0].runs + 1}` : "";
  const x = inn.extras;

  return (
    <section className="pane on" id="pane-scorecard" role="tabpanel" aria-labelledby="tab-scorecard">
      <div className="toolbar">
        <Seg label="Innings" opts={innOpts(inns)} cur={String(idx)} onPick={setCur} />
        {note && <span className="r" style={{ fontSize: "12.5px", color: "var(--muted)" }}>{note}</span>}
      </div>

      <div className="box">
        <div className="box-h"><h3>Batting</h3><span className="sub">{inn.team.name}, {inn.runs}/{inn.wkts} in {oversText(inn.legal)} overs</span></div>
        <div className="tw">
          <table className="sc-t">
            <thead><tr><th>Batter</th><th className="l">Dismissal</th><th>R</th><th>B</th><th>4s</th><th>6s</th><th>SR</th></tr></thead>
            <tbody>
              {inn.batting.map((b) => {
                const tag = roleTag(roleOf(b.playerId));
                const notOut = /not\s*out/i.test(b.outString ?? "") || b.status === "STRIKER" || b.status === "NON_STRIKER";
                return (
                  <tr key={b.playerId}>
                    <td className="t">
                      <NameLink id={b.playerId} onOpen={ctx.openPlayer}>{personName(b.playerFirstName, b.playerLastName)}</NameLink>
                      {notOut ? "*" : ""}
                      {tag && <span className="tag">{tag}</span>}
                      {ctx.potmId === b.playerId && <span className="tag star">POTM</span>}
                    </td>
                    <td className="s l">{b.outString || (notOut ? "not out" : "")}</td>
                    <td className="big">{b.run ?? 0}</td>
                    <td>{b.balls ?? 0}</td>
                    <td>{b.fours ?? 0}</td>
                    <td>{b.six ?? 0}</td>
                    <td>{b.strikeRate != null ? Number(b.strikeRate).toFixed(1) : strikeRate(b.run ?? 0, b.balls ?? 0)}</td>
                  </tr>
                );
              })}
              <tr className="foot">
                <td>Extras</td>
                <td className="l" style={{ fontFamily: "var(--sans)", fontSize: "12.5px", color: "var(--muted)" }}>b {x.b}, lb {x.lb}, w {x.wd}, nb {x.nb}{x.pen ? `, pen ${x.pen}` : ""}</td>
                <td className="n">{x.t}</td>
                <td colSpan={4} />
              </tr>
              <tr className="tot">
                <td>Total</td>
                <td className="l" style={{ fontSize: "12.5px", fontWeight: 600, color: "var(--muted)" }}>{oversText(inn.legal)} overs, run rate {runRate(inn.runs, inn.legal).toFixed(2)}</td>
                <td><span className="n">{inn.runs}/{inn.wkts}</span></td>
                <td colSpan={4} />
              </tr>
            </tbody>
          </table>
        </div>
        {dnb.length > 0 && (
          <div className="dnb">
            <b>Did not bat</b>
            {dnb.map((p, i) => (
              <span key={p.playerId}>
                {i > 0 ? ", " : ""}
                <NameLink id={p.playerId} onOpen={ctx.openPlayer}>{personName(p.playerFirstName, p.playerLastName)}</NameLink>
              </span>
            ))}
          </div>
        )}
      </div>

      {inn.bowling.length > 0 && (
        <div className="box">
          <div className="box-h"><h3>Bowling</h3><span className="sub">{inn.opp.name}</span></div>
          <div className="tw">
            <table className="sc-t">
              <thead><tr><th>Bowler</th><th>O</th><th>M</th><th>R</th><th>W</th><th>Econ</th><th>Dots</th><th>Wd</th><th>Nb</th></tr></thead>
              <tbody>
                {inn.bowling.map((b) => (
                  <tr key={b.id ?? b.playerId}>
                    <td className="t"><NameLink id={b.playerId} onOpen={ctx.openPlayer}>{personName(b.playerFirstName, b.playerLastName)}</NameLink></td>
                    <td>{bowlerOvers(b)}</td>
                    <td>{b.maiden ?? 0}</td>
                    <td>{b.runs ?? 0}</td>
                    <td className="big">{b.wickets ?? 0}</td>
                    <td>{b.economy != null ? Number(b.economy).toFixed(2) : (bowlerBalls(b) ? ((b.runs ?? 0) / (bowlerBalls(b) / 6)).toFixed(2) : "-")}</td>
                    <td>{b.dotBall ?? 0}</td>
                    <td>{b.wide ?? 0}</td>
                    <td>{b.noBall ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(comm?.partnerships.length || comm?.fow.length || fallRaw.length) ? (
        <div className="two">
          {comm && comm.partnerships.length > 0 && (
            <div className="box">
              <div className="box-h"><h3>Partnerships</h3></div>
              <ul className="pship">
                {comm.partnerships.map((p, j) => (
                  <li key={j}>
                    <div className="who">{ord(j + 1)} wicket <span>{p.a}{p.b ? ` & ${p.b}` : ""}</span></div>
                    <div className="v">{p.runs}{p.unbeaten ? "*" : ""}<small>{p.balls} balls</small></div>
                    <div className="tr"><i style={{ width: `${(Math.max(p.runs, 0) / maxPartner) * 100}%`, background: inn.team.color }} /></div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="box">
            <div className="box-h"><h3>Fall of wickets</h3></div>
            {comm && comm.fow.length > 0 ? (
              <ul className="fow">
                {comm.fow.map((f) => (
                  <li key={f.w}>
                    <span className="s">{f.score}/{f.w}</span>
                    <span className="w">
                      <NameLink id={f.playerId} onOpen={ctx.openPlayer}>{f.who}</NameLink>{" "}
                      {f.how && <span style={{ fontWeight: 500, color: "var(--muted)", fontSize: "12px" }}>{f.how}</span>}
                    </span>
                    <span className="o">{f.at} ov</span>
                  </li>
                ))}
              </ul>
            ) : fallRaw.length > 0 ? (
              <ul className="fow">
                {fallRaw.map((f, i) => (
                  <li key={i}><span className="s">{String(f)}</span><span className="w" /><span className="o" /></li>
                ))}
              </ul>
            ) : (
              <Empty>No wickets fell.</Empty>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ScorecardPane;
